import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

/**
 * A field that searches SAP as the user types (G/L accounts, vendors) and keeps one choice.
 * `search` is called 400 ms after the last keystroke with at least 2 characters.
 */
export interface PickerOption { code: string; name: string; hint?: string }

interface Props {
  id: string;
  label: string;
  value: PickerOption | null;
  onChange: (value: PickerOption | null) => void;
  search: (q: string) => Promise<PickerOption[]>;
  helperText?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
}

const SearchPicker: React.FC<Props> = ({ id, label, value, onChange, search, helperText, disabled, required, placeholder, size = 'small' }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    const q = input.trim();
    const shown = value ? `${value.code} ${value.name}`.trim() : '';
    if (q.length < 2 || q === shown) return undefined;
    const ticket = ++request.current;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      search(q)
        .then((rows) => { if (ticket === request.current) setOptions(rows); })
        .catch((e: any) => { if (ticket === request.current) setError(e?.response?.data?.message || 'The search failed'); })
        .finally(() => { if (ticket === request.current) setLoading(false); });
    }, 400);
    return () => clearTimeout(timer);
  }, [input, search, value]);

  return (
    <Autocomplete
      id={id}
      size={size}
      disabled={disabled}
      value={value}
      options={value && !options.some((o) => o.code === value.code) ? [value, ...options] : options}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(o) => `${o.code} ${o.name}`.trim()}
      renderOption={(props, o) => (
        <li {...props} key={o.code}>
          <span>
            <strong>{o.code}</strong> {o.name}
            {o.hint ? <span style={{ display: 'block', fontSize: 12, color: '#5f6368' }}>{o.hint}</span> : null}
          </span>
        </li>
      )}
      onChange={(_e, v) => onChange(v)}
      onInputChange={(_e, v) => setInput(v)}
      loading={loading}
      noOptionsText={input.trim().length < 2 ? 'Type 2 or more characters' : 'Nothing found'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={!!error}
          helperText={error || helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchPicker;
